import React, { useState, useEffect } from 'react';
import { 
  Plus, Search, Edit, Trash2, Eye, 
  ChevronDown, X, Camera, Loader2,
  Filter, Calendar, Users, Target,
  BookOpen, ArrowUpDown, ChevronRight,
  AlertCircle
} from 'lucide-react';
import ImageUpload from '../components/ImageUpload';
import { format } from 'date-fns';

const SuccessStoryList = () => {
  const [stories, setStories] = useState([]);
  const [filteredStories, setFilteredStories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingStory, setEditingStory] = useState(null);
  const [viewingStory, setViewingStory] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [sortBy, setSortBy] = useState('newest');
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [storyToDelete, setStoryToDelete] = useState(null);
  const [error, setError] = useState('');
  const [currentStep, setCurrentStep] = useState(0);

  // Form state with validation
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: '',
    tags: [],
    images: [],
    beforeAfter: {
      before: {
        metrics: [],
        images: [],
        description: ''
      },
      after: {
        metrics: [],
        images: [],
        description: ''
      }
    },
    status: 'draft'
  });

  const [formErrors, setFormErrors] = useState({});

  const categories = [
    { value: 'academic', label: 'Academic Achievement' },
    { value: 'career', label: 'Career Growth' },
    { value: 'entrepreneurship', label: 'Entrepreneurship' },
    { value: 'research', label: 'Research & Innovation' },
    { value: 'social_impact', label: 'Social Impact' },
    { value: 'other', label: 'Other' }
  ];

  const formSteps = [
    {
      id: 'basics',
      title: 'Basic Info',
      icon: BookOpen,
      description: 'Story details',
    },
    {
      id: 'before',
      title: 'Before',
      icon: Target,
      description: 'Initial state',
    },
    {
      id: 'after',
      title: 'After',
      icon: Target,
      description: 'Results & Impact',
    }
  ];

  useEffect(() => {
    fetchStories();
  }, []);

  useEffect(() => {
    filterStories();
  }, [stories, searchQuery, selectedCategory, selectedStatus, sortBy]);

  const fetchStories = async () => {
    try {
      setError('');
      const response = await fetch(`${process.env.REACT_APP_BASE_URL}/api/college/success-stories`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (!response.ok) throw new Error('Failed to fetch stories');
      
      const data = await response.json();
      setStories(data);
      setFilteredStories(data);
    } catch (err) {
      setError('Failed to load success stories');
      console.error('Error fetching stories:', err);
    } finally {
      setLoading(false);
    }
  };

  const filterStories = () => {
    let filtered = [...stories];

    // Search filter
    if (searchQuery) {
      filtered = filtered.filter(story => 
        story.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        story.description.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    // Category filter
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(story => story.category === selectedCategory);
    }

    // Status filter
    if (selectedStatus !== 'all') {
      filtered = filtered.filter(story => story.status === selectedStatus);
    }

    // Sorting
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'newest':
          return new Date(b.createdAt) - new Date(a.createdAt);
        case 'oldest':
          return new Date(a.createdAt) - new Date(b.createdAt);
        case 'title':
          return a.title.localeCompare(b.title);
        default:
          return 0;
      }
    });

    setFilteredStories(filtered);
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.title.trim()) errors.title = 'Title is required';
    if (!formData.description.trim()) errors.description = 'Description is required';
    if (!formData.category) errors.category = 'Category is required';
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    try {
      setError('');
      const url = editingStory 
        ? `${process.env.REACT_APP_BASE_URL}/api/college/success-stories/${editingStory._id}`
        : `${process.env.REACT_APP_BASE_URL}/api/college/success-stories`;

      const response = await fetch(url, {
        method: editingStory ? 'PUT' : 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (!response.ok) throw new Error(`Failed to ${editingStory ? 'update' : 'create'} story`);

      await fetchStories();
      setShowModal(false);
      resetForm();
    } catch (err) {
      setError(err.message);
      console.error('Error saving story:', err);
    }
  };

  const handleDelete = async (id) => {
    try {
      setError('');
      const response = await fetch(`${process.env.REACT_APP_BASE_URL}/api/college/success-stories/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) throw new Error('Failed to delete story');

      await fetchStories();
      setShowDeleteDialog(false);
      setStoryToDelete(null);
    } catch (err) {
      setError('Failed to delete story');
      console.error('Error deleting story:', err);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      category: '',
      tags: [],
      images: [],
      beforeAfter: {
        before: {
          metrics: [],
          images: [],
          description: ''
        },
        after: {
          metrics: [],
          images: [],
          description: ''
        }
      },
      status: 'draft'
    });
    setFormErrors({});
    setEditingStory(null);
    setCurrentStep(0);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="flex flex-col items-center space-y-4">
          <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
          <p className="text-gray-600 animate-pulse">Loading stories...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="relative bg-gradient-to-br from-blue-900 via-indigo-900 to-purple-900">
        <div className="absolute inset-0 bg-grid-white/[0.05] bg-[size:20px_20px]" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="flex flex-col items-center text-center">
            <div className="p-3 bg-blue-50 rounded-xl mb-6">
              <BookOpen className="h-6 w-6 text-blue-600" />
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Success Stories
            </h1>
            <p className="text-lg text-white/80 max-w-2xl">
              Showcase and celebrate the achievements of your community
            </p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 mt-8">
        {/* Filters & Search Bar */}
        <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search stories..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Category Filter */}
            <div className="relative">
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="w-full pl-4 pr-10 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 appearance-none"
              >
                <option value="all">All Categories</option>
                {categories.map(cat => (
                  <option key={cat.value} value={cat.value}>{cat.label}</option>
                ))}
              </select>
              <ChevronDown className="absolute right-3 top-2.5 h-5 w-5 text-gray-400 pointer-events-none" />
            </div>

            {/* Status Filter */}
            <div className="relative">
              <select
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
                className="w-full pl-4 pr-10 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 appearance-none"
              >
                <option value="all">All Status</option>
                <option value="draft">Draft</option>
                <option value="published">Published</option>
              </select>
              <ChevronDown className="absolute right-3 top-2.5 h-5 w-5 text-gray-400 pointer-events-none" />
            </div>

            {/* Sort By */}
            <div className="relative">
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="w-full pl-4 pr-10 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 appearance-none"
              >
                <option value="newest">Newest First</option>
                <option value="oldest">Oldest First</option>
                <option value="title">Title</option>
              </select>
              <ChevronDown className="absolute right-3 top-2.5 h-5 w-5 text-gray-400 pointer-events-none" />
            </div>
          </div>

          {/* Action Button */}
          <div className="mt-4 flex justify-end">
            <button
              onClick={() => {
                resetForm();
                setShowModal(true);
              }}
              className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Plus className="w-5 h-5 mr-2" />
              New Story
            </button>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center text-red-700">
            <AlertCircle className="h-5 w-5 mr-2" />
            {error}
          </div>
        )}

        {/* Stories Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          {filteredStories.map(story => (
            <div key={story._id} className="bg-white rounded-xl shadow-sm overflow-hidden hover:shadow-md transition-shadow">
              <div className="aspect-video bg-gray-100 relative">
                {story.images[0] ? (
                  <img
                    src={story.images[0]}
                    alt={story.title}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Camera className="w-8 h-8 text-gray-400" />
                  </div>
                )}
                <div className="absolute top-2 right-2">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium
                    ${story.status === 'published' 
                      ? 'bg-green-100 text-green-800'
                      : 'bg-yellow-100 text-yellow-800'
                    }`}
                  >
                    {story.status === 'published' ? 'Published' : 'Draft'}
                  </span>
                </div>
              </div>

              <div className="p-6">
                <div className="flex items-start justify-between">
                  <div className="space-y-2">
                    <h3 className="text-lg font-semibold line-clamp-2">{story.title}</h3>
                    <span className="inline-block px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-800">
                      {categories.find(cat => cat.value === story.category)?.label || story.category}
                    </span>
                    <p className="text-sm text-gray-600 line-clamp-2">{story.description}</p>
                  </div>
                </div>

                <div className="mt-4 flex justify-end space-x-2">
                <button
                      onClick={() => {
                        setViewingStory(story);
                        setShowModal(true);
                      }}
                      className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                      <Eye className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => {
                        setEditingStory(story);
                        setFormData(story);
                        setShowModal(true);
                      }}
                      className="p-2 text-blue-500 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-colors"
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => {
                        setStoryToDelete(story);
                        setShowDeleteDialog(true);
                      }}
                      className="p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Empty State */}
          {filteredStories.length === 0 && (
            <div className="text-center py-12">
              <BookOpen className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No stories found</h3>
              <p className="mt-1 text-sm text-gray-500">
                {stories.length === 0 
                  ? "Get started by creating a new success story"
                  : "Try adjusting your filters"}
              </p>
            </div>
          )}
        </div>
   

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="p-6 border-b border-gray-200">
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-semibold">
                  {editingStory ? 'Edit Story' : viewingStory ? 'View Story' : 'New Story'}
                </h2>
                <button
                  onClick={() => {
                    setShowModal(false);
                    resetForm();
                  }}
                  className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Progress Steps - Show only in edit/create mode */}
              {!viewingStory && (
                <div className="mt-6 flex justify-between">
                  {formSteps.map((step, index) => {
                    const Icon = step.icon;
                    return (
                      <div 
                        key={step.id}
                        onClick={() => setCurrentStep(index)}
                        className="flex flex-col items-center cursor-pointer"
                      >
                        <div className={`flex items-center justify-center w-12 h-12 rounded-full mb-2
                          transition-all duration-200 ${
                            index <= currentStep
                              ? "bg-blue-50 border-2 border-blue-500"
                              : "bg-gray-100 border-2 border-transparent"
                          }`}
                        >
                          <Icon className={`h-5 w-5 ${
                            index <= currentStep ? "text-blue-600" : "text-gray-400"
                          }`} />
                        </div>
                        <div className="text-center">
                          <p className={`text-sm font-medium ${
                            index <= currentStep ? "text-gray-900" : "text-gray-500"
                          }`}>
                            {step.title}
                          </p>
                          <p className="text-xs text-gray-500 hidden md:block">
                            {step.description}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Modal Content */}
            <div className="p-6">
              {viewingStory ? (
                // View Mode
                <div className="space-y-6">
                  {/* Story Images */}
                  {viewingStory.images.length > 0 && (
                    <div className="grid grid-cols-3 gap-4">
                      {viewingStory.images.map((image, index) => (
                        <div key={index} className="aspect-video rounded-lg overflow-hidden bg-gray-100">
                          <img
                            src={image}
                            alt={`Story image ${index + 1}`}
                            className="w-full h-full object-cover"
                          />
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Story Details */}
                  <div className="space-y-4">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">{viewingStory.title}</h3>
                      <div className="mt-2 flex items-center space-x-4">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          {categories.find(cat => cat.value === viewingStory.category)?.label}
                        </span>
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium
                          ${viewingStory.status === 'published' 
                            ? 'bg-green-100 text-green-800'
                            : 'bg-yellow-100 text-yellow-800'
                          }`}
                        >
                          {viewingStory.status === 'published' ? 'Published' : 'Draft'}
                        </span>
                      </div>
                    </div>
                    <p className="text-gray-600">{viewingStory.description}</p>
                  </div>

                  {/* Before & After */}
                  {(viewingStory.beforeAfter?.before || viewingStory.beforeAfter?.after) && (
                    <div className="grid md:grid-cols-2 gap-6">
                      {/* Before Section */}
                      {viewingStory.beforeAfter?.before && (
                        <div className="space-y-4">
                          <h4 className="font-semibold text-gray-900">Before</h4>
                          <p className="text-gray-600">{viewingStory.beforeAfter.before.description}</p>
                          
                          {/* Before Metrics */}
                          {viewingStory.beforeAfter.before.metrics.length > 0 && (
                            <div className="space-y-2">
                              {viewingStory.beforeAfter.before.metrics.map((metric, index) => (
                                <div key={index} className="flex justify-between bg-gray-50 p-3 rounded-lg">
                                  <span className="font-medium text-gray-700">{metric.key}</span>
                                  <span className="text-gray-900">{metric.value}</span>
                                </div>
                              ))}
                            </div>
                          )}

                          {/* Before Images */}
                          {viewingStory.beforeAfter.before.images.length > 0 && (
                            <div className="grid grid-cols-2 gap-2">
                              {viewingStory.beforeAfter.before.images.map((image, index) => (
                                <div key={index} className="aspect-video rounded-lg overflow-hidden bg-gray-100">
                                  <img
                                    src={image}
                                    alt={`Before image ${index + 1}`}
                                    className="w-full h-full object-cover"
                                  />
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}

                      {/* After Section */}
                      {viewingStory.beforeAfter?.after && (
                        <div className="space-y-4">
                          <h4 className="font-semibold text-gray-900">After</h4>
                          <p className="text-gray-600">{viewingStory.beforeAfter.after.description}</p>
                          
                          {/* After Metrics */}
                          {viewingStory.beforeAfter.after.metrics.length > 0 && (
                            <div className="space-y-2">
                              {viewingStory.beforeAfter.after.metrics.map((metric, index) => (
                                <div key={index} className="flex justify-between bg-gray-50 p-3 rounded-lg">
                                  <span className="font-medium text-gray-700">{metric.key}</span>
                                  <span className="text-gray-900">{metric.value}</span>
                                </div>
                              ))}
                            </div>
                          )}

                          {/* After Images */}
                          {viewingStory.beforeAfter.after.images.length > 0 && (
                            <div className="grid grid-cols-2 gap-2">
                              {viewingStory.beforeAfter.after.images.map((image, index) => (
                                <div key={index} className="aspect-video rounded-lg overflow-hidden bg-gray-100">
                                  <img
                                    src={image}
                                    alt={`After image ${index + 1}`}
                                    className="w-full h-full object-cover"
                                  />
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ) : (
                // Edit/Create Form
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Basic Info Step */}
                  <div className={currentStep === 0 ? "block" : "hidden"}>
                    <div className="space-y-6">
                      {/* Title */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Title
                        </label>
                        <input
                          type="text"
                          value={formData.title}
                          onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                          className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500
                            ${formErrors.title ? 'border-red-500' : 'border-gray-300'}`}
                          placeholder="Enter story title"
                        />
                        {formErrors.title && (
                          <p className="mt-1 text-sm text-red-500">{formErrors.title}</p>
                        )}
                      </div>

                      {/* Category */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Category
                        </label>
                        <select
                          value={formData.category}
                          onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                          className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500
                            ${formErrors.category ? 'border-red-500' : 'border-gray-300'}`}
                        >
                          <option value="">Select Category</option>
                          {categories.map(cat => (
                            <option key={cat.value} value={cat.value}>{cat.label}</option>
                          ))}
                        </select>
                        {formErrors.category && (
                          <p className="mt-1 text-sm text-red-500">{formErrors.category}</p>
                        )}
                      </div>

                      {/* Description */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Description
                        </label>
                        <textarea
                          value={formData.description}
                          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                          rows={4}
                          className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500
                            ${formErrors.description ? 'border-red-500' : 'border-gray-300'}`}
                          placeholder="Describe the success story"
                        />
                        {formErrors.description && (
                          <p className="mt-1 text-sm text-red-500">{formErrors.description}</p>
                        )}
                      </div>

                      {/* Story Images */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Story Images
                        </label>
                        <div className="grid grid-cols-3 gap-4">
                          {[0, 1, 2].map((index) => (
                            <ImageUpload
                              key={index}
                              existingImage={formData.images[index]}
                              onImageUpload={(url) => {
                                const newImages = [...formData.images];
                                newImages[index] = url;
                                setFormData({ ...formData, images: newImages });
                              }}
                            />
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Before Step */}
                  <div className={currentStep === 1 ? "block" : "hidden"}>
                    <div className="space-y-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Before Description
                        </label>
                        <textarea
                          value={formData.beforeAfter.before.description}
                          onChange={(e) => setFormData({
                            ...formData,
                            beforeAfter: {
                              ...formData.beforeAfter,
                              before: {
                                ...formData.beforeAfter.before,
                                description: e.target.value
                              }
                            }
                          })}
                          rows={3}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                          placeholder="Describe the initial state"
                        />
                      </div>

                      {/* Before Metrics */}
                      <div className="space-y-4">
                        <div className="flex items-center justify-between">
                          <label className="block text-sm font-medium text-gray-700">
                            Before Metrics
                          </label>
                          <button
                            type="button"
                            onClick={() => {
                              const newMetrics = [...formData.beforeAfter.before.metrics];
                              newMetrics.push({ key: '', value: '' });
                              setFormData({
                                ...formData,
                                beforeAfter: {
                                  ...formData.beforeAfter,
                                  before: {
                                    ...formData.beforeAfter.before,
                                    metrics: newMetrics
                                  }
                                }
                              });
                            }}
                            className="text-sm text-blue-600 hover:text-blue-700"
                          >
                            + Add Metric
                          </button>
                        </div>
                        
                        {formData.beforeAfter.before.metrics.map((metric, index) => (
                          <div key={index} className="flex gap-4">
                            <input
                              type="text"
                              placeholder="Metric Name"
                              value={metric.key}
                              onChange={(e) => {
                                const newMetrics = [...formData.beforeAfter.before.metrics];
                                newMetrics[index].key = e.target.value;
                                setFormData({
                                  ...formData,
                                  beforeAfter: {
                                    ...formData.beforeAfter,
                                    before: {
                                      ...formData.beforeAfter.before,
                                      metrics: newMetrics
                                    }
                                  }
                                });
                              }}
                              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                            />
                            <input
                              type="text"
                              placeholder="Value"
                              value={metric.value}
                              onChange={(e) => {
                                const newMetrics = [...formData.beforeAfter.before.metrics];
                                newMetrics[index].value = e.target.value;
                                setFormData({
                                  ...formData,
                                  beforeAfter: {
                                    ...formData.beforeAfter,
                                    before: {
                                      ...formData.beforeAfter.before,
                                      metrics: newMetrics
                                    }
                                  }
                                });
                              }}
                              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                            />
                            <button
                              type="button"
                              onClick={() => {
                                const newMetrics = formData.beforeAfter.before.metrics.filter((_, i) => i !== index);
                                setFormData({
                                  ...formData,
                                  beforeAfter: {
                                    ...formData.beforeAfter,
                                    before: {
                                      ...formData.beforeAfter.before,
                                      metrics: newMetrics
                                    }
                                  }
                                });
                              }}
                              className="p-2 text-red-500 hover:text-red-700"
                            >
                              <X className="h-5 w-5" />
                            </button>
                          </div>
                        ))}
                      </div>

                      {/* Before Images */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Before Images
                        </label>
                        <div className="grid grid-cols-2 gap-4">
                          {[0, 1].map((index) => (
                            <ImageUpload
                              key={index}
                              existingImage={formData.beforeAfter.before.images[index]}
                              onImageUpload={(url) => {
                                const newImages = [...formData.beforeAfter.before.images];
                                newImages[index] = url;
                                setFormData({
                                  ...formData,
                                  beforeAfter: {
                                    ...formData.beforeAfter,
                                    before: {
                                      ...formData.beforeAfter.before,
                                      images: newImages
                                    }
                                  }
                                });
                              }}
                            />
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* After Step */}
                  <div className={currentStep === 2 ? "block" : "hidden"}>
                    <div className="space-y-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          After Description
                        </label>
                        <textarea
                          value={formData.beforeAfter.after.description}
                          onChange={(e) => setFormData({
                            ...formData,
                            beforeAfter: {
                              ...formData.beforeAfter,
                              after: {
                                ...formData.beforeAfter.after,
                                description: e.target.value
                              }
                            }
                          })}
                          rows={3}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                          placeholder="Describe the results and impact"
                        />
                      </div>

                      {/* After Metrics */}
                      <div className="space-y-4">
                        <div className="flex items-center justify-between">
                          <label className="block text-sm font-medium text-gray-700">
                            After Metrics
                          </label>
                          <button
                            type="button"
                            onClick={() => {
                              const newMetrics = [...formData.beforeAfter.after.metrics];
                              newMetrics.push({ key: '', value: '' });
                              setFormData({
                                ...formData,
                                beforeAfter: {
                                  ...formData.beforeAfter,
                                  after: {
                                    ...formData.beforeAfter.after,
                                    metrics: newMetrics
                                  }
                                }
                              });
                            }}
                            className="text-sm text-blue-600 hover:text-blue-700"
                          >
                            + Add Metric
                          </button>
                        </div>
                        
                        {formData.beforeAfter.after.metrics.map((metric, index) => (
                          <div key={index} className="flex gap-4">
                            <input
                              type="text"
                              placeholder="Metric Name"
                              value={metric.key}
                              onChange={(e) => {
                                const newMetrics = [...formData.beforeAfter.after.metrics];
                                newMetrics[index].key = e.target.value;
                                setFormData({
                                  ...formData,
                                  beforeAfter: {
                                    ...formData.beforeAfter,
                                    after: {
                                      ...formData.beforeAfter.after,
                                      metrics: newMetrics
                                    }
                                  }
                                });
                              }}
                              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                            />
                            <input
                              type="text"
                              placeholder="Value"
                              value={metric.value}
                              onChange={(e) => {
                                const newMetrics = [...formData.beforeAfter.after.metrics];
                                newMetrics[index].value = e.target.value;
                                setFormData({
                                  ...formData,
                                  beforeAfter: {
                                    ...formData.beforeAfter,
                                    after: {
                                      ...formData.beforeAfter.after,
                                      metrics: newMetrics
                                    }
                                  }
                                });
                              }}
                              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                            />
                            <button
                              type="button"
                              onClick={() => {
                                const newMetrics = formData.beforeAfter.after.metrics.filter((_, i) => i !== index);
                                setFormData({
                                  ...formData,
                                  beforeAfter: {
                                    ...formData.beforeAfter,
                                    after: {
                                      ...formData.beforeAfter.after,
                                      metrics: newMetrics
                                    }
                                  }
                                });
                              }}
                              className="p-2 text-red-500 hover:text-red-700"
                            >
                              <X className="h-5 w-5" />
                            </button>
                          </div>
                        ))}
                      </div>

                      {/* After Images */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          After Images
                        </label>
                        <div className="grid grid-cols-2 gap-4">
                          {[0, 1].map((index) => (
                            <ImageUpload
                              key={index}
                              existingImage={formData.beforeAfter.after.images[index]}
                              onImageUpload={(url) => {
                                const newImages = [...formData.beforeAfter.after.images];
                                newImages[index] = url;
                                setFormData({
                                  ...formData,
                                  beforeAfter: {
                                    ...formData.beforeAfter,
                                    after: {
                                      ...formData.beforeAfter.after,
                                      images: newImages
                                    }
                                  }
                                });
                              }}
                            />
                          ))}
                        </div>
                      </div>

                      {/* Story Status */}
                      <div className="border-t pt-6">
                        <div className="flex items-center justify-between">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Story Status
                            </label>
                            <div className="flex items-center space-x-4">
                              <label className="inline-flex items-center">
                                <input
                                  type="radio"
                                  value="draft"
                                  checked={formData.status === 'draft'}
                                  onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                                  className="form-radio h-4 w-4 text-blue-600"
                                />
                                <span className="ml-2 text-sm text-gray-700">Draft</span>
                              </label>
                              <label className="inline-flex items-center">
                                <input
                                  type="radio"
                                  value="published"
                                  checked={formData.status === 'published'}
                                  onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                                  className="form-radio h-4 w-4 text-blue-600"
                                />
                                <span className="ml-2 text-sm text-gray-700">Published</span>
                              </label>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Navigation Buttons */}
                  <div className="flex items-center justify-between pt-6 border-t">
                    <button
                      type="button"
                      onClick={() => {
                        if (currentStep > 0) setCurrentStep(currentStep - 1);
                      }}
                      className={`px-4 py-2 text-sm font-medium rounded-lg border border-gray-300
                        ${currentStep === 0
                          ? 'text-gray-400 cursor-not-allowed'
                          : 'text-gray-700 hover:bg-gray-50'
                        }`}
                      disabled={currentStep === 0}
                    >
                      Previous
                    </button>

                    <div className="flex gap-3">
                      <button
                        type="button"
                        onClick={() => {
                          setShowModal(false);
                          resetForm();
                        }}
                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border 
                        border-gray-300 rounded-lg hover:bg-gray-50"
                      >
                        Cancel
                      </button>

                      {currentStep < formSteps.length - 1 ? (
                        <button
                          type="button"
                          onClick={() => setCurrentStep(currentStep + 1)}
                          className="px-4 py-2 text-sm font-medium text-white bg-blue-600 
                          rounded-lg hover:bg-blue-700"
                        >
                          Next
                        </button>
                      ) : (
                        <button
                          type="submit"
                          className="px-4 py-2 text-sm font-medium text-white bg-blue-600 
                          rounded-lg hover:bg-blue-700"
                        >
                          {editingStory ? 'Update Story' : 'Create Story'}
                        </button>
                      )}
                    </div>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      {showDeleteDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-sm w-full">
            <h3 className="text-lg font-medium text-gray-900 mb-2">Delete Story</h3>
            <p className="text-sm text-gray-500">
              Are you sure you want to delete this story? This action cannot be undone.
            </p>
            <div className="mt-6 flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowDeleteDialog(false);
                  setStoryToDelete(null);
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border 
                border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  if (storyToDelete) handleDelete(storyToDelete._id);
                }}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 
                rounded-lg hover:bg-red-700"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

    </div>

  );
};

export default SuccessStoryList;